import type { Control, FieldPath, UseFormRegister } from "react-hook-form";

import { motion } from "framer-motion";
import { toast } from "sonner";
import { z } from "zod";

import type { Step } from "@/hooks/use-multi-step";

import { useUpdateUserProfile } from "@/api/users-query";
import { useCities, useCountries, useStates } from "@/api/util";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useMultiStepForm } from "@/hooks/use-multi-step";
import useAuthStore from "@/store/auth";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";

export const Route = createFileRoute("/form")({
	beforeLoad: async () => {
		const { user } = useAuthStore.getState();
		if (!user) {
			throw redirect({ to: "/sign-in" });
		}
	},
	component: RouteComponent
});

export const FormDataSchema = z.object({
	businessName: z.string().min(1, "Business name is required"),
	businessPhone: z.string().min(1, "Business phone is required"),
	businessStreet: z.string().min(1, "Business street is required"),
	country: z.string().min(1, "Country is required"),
	state: z.string().min(1, "State is required"),
	city: z.string().min(1, "City is required")
});

const steps: Step<typeof FormDataSchema>[] = [
	{
		id: "Step 1",
		name: "Business Information",
		fields: ["businessName", "businessPhone"] as const
	},
	{
		id: "Step 2",
		name: "Address",
		fields: ["businessStreet", "country", "state", "city"] as const
	}
];

export default function RouteComponent() {
	const navigate = useNavigate();
	const form = useMultiStepForm(
		{ resolver: zodResolver(FormDataSchema), defaultValues: {
			businessName: "",
			businessPhone: "",
			businessStreet: "",
			country: "",
			state: "",
			city: ""
		} },
		{ schema: FormDataSchema, steps },
		processForm
	);

	const delta = form.currentStep - form.previousStep;
	const updateBusiness = useUpdateUserProfile();

	async function processForm(data: z.infer<typeof FormDataSchema>) {
		await updateBusiness.mutateAsync(data);
	}

	const countries = useCountries();

	const { data: states } = useStates(form.watch("country"));

	const { data: cities } = useCities({
		stateId: form.watch("state"),
		countryId: form.watch("country")
	});

	if (updateBusiness.isSuccess) {
		toast.success("Business information updated successfully");
		form.reset();
		return navigate({ to: "/get-started" });
	}

	if (updateBusiness.isError) {
		toast.error("Failed to update business information");
	}

	if (updateBusiness.isPending) {
		return (
			<div className="flex h-screen flex-col items-center justify-center gap-4">
				<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
				<p className="text-muted-foreground">Updating business information...</p>
			</div>
		);
	}

	return (
		<section className="flex flex-col justify-between p-24 max-sm:p-8">

			<nav aria-label="Progress">
				<ol role="list" className="space-y-4 md:flex md:space-x-8 md:space-y-0">

					{steps.map((step, index) => (
						<li key={step.name} className="md:flex-1">
							{form.currentStep > index
								? (
										<div className="group flex w-full flex-col border-l-4 border-primary py-2 pl-4 transition-colors md:border-l-0 md:border-t-4 md:pb-0 md:pl-0 md:pt-4">
											<span className="text-sm font-medium text-primary transition-colors ">
												{step.id}
											</span>
											<span className="text-sm font-medium">{step.name}</span>
										</div>
									)
								: form.currentStep === index
									? (
											<div
												className="flex w-full flex-col border-l-4 border-primary py-2 pl-4 md:border-l-0 md:border-t-4 md:pb-0 md:pl-0 md:pt-4"
												aria-current="step"
											>
												<span className="text-sm font-medium text-primary">
													{step.id}
												</span>
												<span className="text-sm font-medium">{step.name}</span>
											</div>
										)
									: (
											<div className="group flex w-full flex-col border-l-4 border-gray-200 py-2 pl-4 transition-colors md:border-l-0 md:border-t-4 md:pb-0 md:pl-0 md:pt-4">
												<span className="text-sm font-medium text-gray-500 transition-colors">
													{step.id}
												</span>
												<span className="text-sm font-medium">{step.name}</span>
											</div>
										)}
						</li>
					))}
				</ol>
			</nav>

			{/* Form */}
			<Form {...form}>
				<form className="mt-12 py-12" onSubmit={form.handleSubmit(processForm)}>
					{form.currentStep === 0 && (
						<motion.div
							initial={{ x: delta >= 0 ? "50%" : "-50%", opacity: 0 }}
							animate={{ x: 0, opacity: 1 }}
							transition={{ duration: 0.3, ease: "easeInOut" }}
						>
							<h2 className="text-base font-semibold leading-7 text-gray-900">
								Business Information
							</h2>
							<p className="mt-1 text-sm leading-6 text-gray-600">
								Provide your Business details.
							</p>
							<div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
								<div className="sm:col-span-3">
									<FormField
										control={form.control}
										name="businessName"
										render={({ field }) => (
											<FormItem>
												<FormLabel
													htmlFor="businessName"
													className="block text-sm font-medium leading-6 text-gray-900"
												>
													Business name
												</FormLabel>
												<FormControl>

													<Input
														id="businessName"
														autoComplete="given-name"
														className="block w-full"
														{...field}
														{...form.register("businessName")}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>
								<div className="sm:col-span-3">
									<FormField
										control={form.control}
										name="businessPhone"
										render={({ field }) => (
											<FormItem>
												<FormLabel
													htmlFor="businessPhone"
													className="block text-sm font-medium leading-6 text-gray-900"
												>
													Business Phone
												</FormLabel>
												<FormControl>

													<Input
														id="businessPhone"
														autoComplete="given-name"
														className="block w-full"
														{...field}
														{...form.register("businessPhone")}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>

							</div>
						</motion.div>
					)}

					{form.currentStep === 1 && (
						<motion.div
							initial={{ x: delta >= 0 ? "50%" : "-50%", opacity: 0 }}
							animate={{ x: 0, opacity: 1 }}
							transition={{ duration: 0.3, ease: "easeInOut" }}
						>
							<h2 className="text-base font-semibold leading-7 text-gray-900">
								Address
							</h2>
							<p className="mt-1 text-sm leading-6 text-gray-600">
								Kindly enter an accurate and current address.
							</p>

							<div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">

								<div className="col-span-full">
									<FormField
										control={form.control}
										name="businessStreet"
										render={({ field }) => (
											<FormItem>
												<FormLabel
													htmlFor="businessStreet"
													className="block text-sm font-medium leading-6 text-gray-900"
												>
													Business Street address
												</FormLabel>
												<FormControl>

													<Input
														type="text"
														id="businessStreet"
														className="block w-full"
														{...field}
														{...form.register("businessStreet")}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>
								<div className="col-span-full">

									<SelectFormField
										control={form.control}
										data={countries.data?.data ?? []}
										label="Country"
										name="country"
										placeholder="Select a country"
										register={form.register}
									/>
								</div>

								<div className="sm:col-span-2 sm:col-start-1">
									<SelectFormField
										control={form.control}
										data={states ?? []}
										label="State / Province"
										name="state"
										placeholder="Select a state"
										register={form.register}
									/>
								</div>
								<div className="sm:col-span-2">
									<SelectFormField
										control={form.control}
										data={cities ?? []}
										label="City"
										name="city"
										placeholder="Select a City"
										register={form.register}
									/>
								</div>
							</div>
						</motion.div>
					)}
				</form>
			</Form>

			{/* Navigation */}
			<div className="mt-8 pt-5">
				<div className="flex justify-between">
					<button
						type="button"
						onClick={form.prev}
						disabled={form.currentStep === 0}
						className="rounded bg-white px-2 py-1 text-sm font-semibold text-sky-900 shadow-sm ring-1 ring-inset ring-sky-300 hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-50"
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							fill="none"
							viewBox="0 0 24 24"
							strokeWidth="1.5"
							stroke="currentColor"
							className="h-6 w-6"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								d="M15.75 19.5L8.25 12l7.5-7.5"
							/>
						</svg>
					</button>
					<button
						type="button"
						onClick={form.next}
						disabled={form.currentStep === steps.length}
						className="rounded bg-white px-2 py-1 text-sm font-semibold text-sky-900 shadow-sm ring-1 ring-inset ring-sky-300 hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-50"
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							fill="none"
							viewBox="0 0 24 24"
							strokeWidth="1.5"
							stroke="currentColor"
							className="h-6 w-6"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								d="M8.25 4.5l7.5 7.5-7.5 7.5"
							/>
						</svg>
					</button>
				</div>
			</div>
		</section>
	);
}

type TFormSchema = z.infer<typeof FormDataSchema>;

interface SelectFormFieldProps<T extends { id: string; name: string }> {
	data: T[];
	label: string;
	disabled?: boolean;
	placeholder?: string;
	name: FieldPath<TFormSchema>;
	control: Control<TFormSchema>;
	register: UseFormRegister<TFormSchema>;
}

function SelectFormField<T extends { id: string; name: string }>({
	data,
	label,
	name,
	control,
	register,
	disabled,
	placeholder = "Select"
}:
SelectFormFieldProps<T>) {
	return (
		<FormField
			control={control}
			name={name}
			render={({ field }) => (
				<FormItem>
					<FormLabel
						htmlFor={name}
						className="block text-sm font-medium leading-6 text-gray-900"
					>
						{label}
					</FormLabel>
					<FormControl>

						<Select
							disabled={disabled || !data.length}
							onValueChange={field.onChange}
						>
							{/* This (input) is a hack to display the proper error message */}
							<input className="hidden" {...register(name)} />
							<SelectTrigger>
								<SelectValue placeholder={placeholder} {...field} />
							</SelectTrigger>
							<SelectContent>
								<SelectGroup>

									{data.map(item => (
										<SelectItem key={item.id} value={item.id}>
											{item.name}
										</SelectItem>
									))}

								</SelectGroup>
							</SelectContent>
						</Select>
					</FormControl>
					<FormMessage />
				</FormItem>
			)}
		/>
	);
}
